import type { CommandDeckContinueResolution } from './resolveCommandDeckContinue'
import type {
  CommandDeckPreviewBundle,
  CommandDeckWitnessCategory,
  CommandDeckWitnessConfidence,
  CommandDeckWitnessEvent,
  CommandDeckWitnessFeedModel,
  CommandDeckWitnessSeverity,
  CommandDeckWitnessSource,
  ToolAvailability,
  ToolCardModel,
} from '../contracts/types'
import { getAccessLabel } from '../missionControl/missionControlModel'

const DEFAULT_SPAN_ID = 'session'

const SURFACE_TOOL_IDS = new Set([
  'guided_studio',
  'omnistudio',
  'omnicine',
  'omnicast',
  'scapes_worlds',
  'xr_worlds',
])

export type BuildCommandDeckWitnessFeedInput = {
  bundle: CommandDeckPreviewBundle
  continueResolution: CommandDeckContinueResolution
  unlocked: boolean
  mode: 'live' | 'preview'
}

function availabilitySeverity(availability: ToolAvailability): CommandDeckWitnessSeverity {
  if (availability === 'locked' || availability === 'unavailable' || availability === 'admin_only') {
    return 'blocked'
  }
  if (availability === 'coming_soon' || availability === 'request_access') {
    return 'warning'
  }
  if (availability === 'preview') {
    return 'info'
  }
  return 'ready'
}

function availabilitySummary(tool: ToolCardModel): string {
  switch (tool.availability) {
    case 'available':
    case 'beta':
      return `${tool.label} is available from this access profile.`
    case 'preview':
      return `${tool.label} is preview-only — not full operational access.`
    case 'coming_soon':
      return `${tool.label} is not operational yet on this deck.`
    case 'locked':
    case 'unavailable':
    case 'admin_only':
      return `${tool.label} is locked for the current session.`
    case 'request_access':
      return `${tool.label} requires an access grant before use.`
    default:
      return `${tool.label} readiness: ${tool.availability}.`
  }
}

export function buildCommandDeckWitnessFeed(
  input: BuildCommandDeckWitnessFeedInput
): CommandDeckWitnessFeedModel {
  const { bundle, continueResolution, unlocked, mode } = input
  const events: CommandDeckWitnessEvent[] = []
  let sortOrder = 0

  const push = (
    category: CommandDeckWitnessCategory,
    severity: CommandDeckWitnessSeverity,
    title: string,
    opts: {
      summary?: string
      source: CommandDeckWitnessSource
      confidence: CommandDeckWitnessConfidence
      timestampLabel?: string
    }
  ) => {
    events.push({
      id: `${category}-${sortOrder}`,
      spanId: DEFAULT_SPAN_ID,
      sortOrder: sortOrder++,
      timestampLabel: opts.timestampLabel ?? 'Session',
      severity,
      category,
      title,
      summary: opts.summary,
      source: opts.source,
      confidence: opts.confidence,
    })
  }

  const hold = bundle.personalization.hold
  const accessLabel = getAccessLabel(bundle.access.accessState)

  if (!unlocked || !hold.unlocked) {
    push('access', 'blocked', 'Access held on Command Deck', {
      summary: hold.headline ?? 'Mission interiors are not available for this session.',
      source: 'access',
      confidence: 'observed',
    })
    for (const notice of bundle.notices.slice(0, 2)) {
      push('access', 'info', notice.title, {
        summary: notice.body,
        source: 'staticNotice',
        confidence: 'placeholder',
      })
    }
    return {
      spanLabel: 'Current session',
      events,
    }
  }

  push('access', 'ready', `${accessLabel} session active`, {
    summary:
      mode === 'preview'
        ? `Preview profile: ${bundle.accessProfileId}.`
        : 'Creator interior grant is active for this deck.',
    source: mode === 'preview' ? 'previewProfile' : 'session',
    confidence: 'observed',
  })

  if (mode === 'preview') {
    push('boundary', 'system', 'Preview mode — routing guards apply', {
      summary: 'Unsafe production targets are blocked; Witness reflects preview-safe resolution only.',
      source: 'previewProfile',
      confidence: 'observed',
    })
  }

  const routeTitle = (() => {
    if (continueResolution.kind === 'hold') {
      return 'Hold pathway — no forward primary action'
    }
    if (continueResolution.kind === 'preview_blocked') {
      return 'Primary action: Review next move (preview blocked)'
    }
    if (continueResolution.usesContinueLabel && continueResolution.navigable) {
      return `Primary action: ${continueResolution.label}`
    }
    if (continueResolution.navigable) {
      return `Primary action: ${continueResolution.label}`
    }
    return `Primary action: ${continueResolution.label || 'Review next move'}`
  })()

  const routeSeverity: CommandDeckWitnessSeverity =
    continueResolution.kind === 'hold' || continueResolution.kind === 'preview_blocked'
      ? 'blocked'
      : continueResolution.navigable
        ? 'ready'
        : 'warning'

  push('route', routeSeverity, routeTitle, {
    summary: continueResolution.targetPath
      ? `Resolved route: ${continueResolution.targetPath}`
      : continueResolution.reason,
    source: 'continueResolver',
    confidence: 'derived',
  })

  const activeSurface = bundle.personalization.surface
  if (activeSurface.surfaceId !== 'none') {
    push('surface', 'info', `Focus surface: ${activeSurface.label}`, {
      summary: activeSurface.routePath
        ? `Session alignment: ${activeSurface.routePath}`
        : 'Active surface inferred from mission and routing context.',
      source: 'session',
      confidence: 'derived',
    })
  }

  const notableTools = bundle.tools.filter(
    (t) =>
      SURFACE_TOOL_IDS.has(t.toolId) &&
      t.availability !== 'available' &&
      t.availability !== 'beta'
  )
  for (const tool of notableTools.slice(0, 3)) {
    push('surface', availabilitySeverity(tool.availability), tool.label, {
      summary: availabilitySummary(tool),
      source: 'toolMatrix',
      confidence: 'derived',
    })
  }

  const bnm = bundle.bestNextMove.primary
  push('progress', 'info', bnm.title, {
    summary: bnm.summary,
    source: 'bestNextMove',
    confidence: 'derived',
  })

  const focusId = bundle.progress.currentFocusStageId
  const focusStage = bundle.progress.spine?.find((s) => s.stageId === focusId)
  if (focusStage) {
    push('progress', 'ready', `Progress focus: ${focusStage.label}`, {
      summary: bundle.progress.summary?.headline,
      source: 'bestNextMove',
      confidence: 'derived',
    })
  }

  const missionName = bundle.personalization.mission.missionName
  if (!bundle.personalization.mission.isActive) {
    push('progress', 'warning', 'No active mission selected', {
      summary: 'Best Next Move reflects pathway education until a mission is active.',
      source: 'bestNextMove',
      confidence: 'derived',
    })
  } else if (missionName) {
    push('progress', 'info', `Mission context: ${missionName}`, {
      summary: bundle.personalization.missionContext.progress.summary?.headline,
      source: 'session',
      confidence: 'observed',
    })
  }

  const snapshotEntries = bundle.personalization.activity.entries
  if (
    bundle.personalization.activity.source === 'project_snapshot' &&
    snapshotEntries.length > 0
  ) {
    for (const entry of snapshotEntries.slice(0, 4)) {
      push('progress', 'info', entry.text, {
        summary: entry.sourceSurface ? `Surface: ${entry.sourceSurface}` : undefined,
        source: 'session',
        confidence: 'observed',
        timestampLabel: entry.time,
      })
    }
  }

  push('boundary', 'system', 'Command Deck is read-only on this surface', {
    summary:
      'No compile, handoff, graph writes, or background automation run from the deck — routing only.',
    source: 'session',
    confidence: 'observed',
  })

  const feed: CommandDeckWitnessFeedModel = {
    spanLabel: 'Current session',
    events,
  }

  if (events.length === 0) {
    feed.emptyState = {
      title: 'No chronicle entries for this session',
      summary: 'Deck state did not produce witness events — this is not a live execution log.',
    }
  }

  return feed
}
